import React, { useState, ChangeEvent } from 'react';
import { I } from '../icons-ds';
import { Kbd } from './Kbd';

export type SearchBarProps = {
  id?: string;
  ariaLabel?: string;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  size?: 'md' | 'lg';
  placeholder?: string;
  /** Show a ⌘K hint when empty (the global quick-open shortcut). */
  showShortcut?: boolean;
};

export function SearchBar({
  id,
  ariaLabel,
  value,
  onChange,
  autoFocus,
  size = 'lg',
  placeholder = 'Search phrases, triggers, or content…',
  showShortcut,
}: SearchBarProps) {
  const [focus, setFocus] = useState(false);
  const h = size === 'lg' ? 56 : 44;

  return (
    <div
      className="dp-search-field"
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        height: h, padding: '0 14px',
        background: 'var(--bg-surface)',
        borderRadius: 16,
        border: `1px solid ${focus ? 'var(--accent)' : 'var(--line-border)'}`,
        boxShadow: 'var(--shadow-1)',
        transition: 'all var(--dur-fast) var(--ease-out)',
      }}
    >
      <I.search size={20} style={{ color: focus ? 'var(--accent)' : 'var(--fg-tertiary)' }} />
      <input
        id={id}
        aria-label={ariaLabel || 'Search phrases'}
        type="search"
        autoFocus={autoFocus}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        placeholder={placeholder}
        enterKeyHint="search"
        autoComplete="off"
        autoCorrect="off"
        spellCheck="false"
        autoCapitalize="off"
        style={{
          flex: 1, minWidth: 0, border: 0, outline: 0, boxShadow: 'none', background: 'transparent',
          fontSize: 16, color: 'var(--fg-primary)',
        }}
      />
      {value ? (
        <button
          onClick={() => onChange('')}
          aria-label="Clear"
          style={{
            width: 44, height: 44, flexShrink: 0, marginRight: -8, borderRadius: 999,
            background: 'transparent', color: 'var(--fg-secondary)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            border: 0, cursor: 'pointer', padding: 0,
          }}
        >
          <I.close size={18} stroke={2.4} />
        </button>
      ) : showShortcut ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
          <Kbd>⌘</Kbd><Kbd>K</Kbd>
        </span>
      ) : null}
    </div>
  );
}

export default SearchBar;
